import React, { useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Hexagon, HexGrid, Layout, Text } from "react-hexgrid";
import "./us-states-hex-grid.scss";
import { Button, Modal, Breadcrumbs, ToolTip, useSnackBar } from "@truevault/aquilo";
import { CircularProgress } from "@mui/material";
import {
  ProgressFooterSaveNotification,
  useEditPrivacyCenter,
  useUpdateNoticeApi,
} from "../../../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../../../hooks/usePrivacyCenterNotice";
import { USStateNoticesEditor } from "./USStateNoticesEditors";
import { PrivacyCenterTabsNew } from "../PrivacyCenterTabsNew";
import { ProgressFooter, ProgressFooterActions } from "../../../get-compliant/ProgressFooter";
import {
  EyeIcon,
  PrinterIcon,
  RocketLaunchIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/solid";
import { Redirect, useHistory } from "react-router";
import { ApiRouteHelpers } from "../../../../../common/root/apiRoutes";
import { useDataRetentionModule } from "../../../../components/organisms/data-retention/DataRetentionModule";
import { usePrivacyCenterPreviewPopup } from "../../StayCompliantPrivacyCenterPage";
import moment from "moment";
import {
  PrivacyCenterPublishDatePickerModal,
  usePrivacyCenterPublish,
} from "../../../../components/privacyCenter/PrivacyCenterPublish";
import { useSurvey } from "../../../../../common/hooks/api/useSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../../../surveys/consumerCollectionIncentivesSurvey";
import { PageLoading } from "../../../../components";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../PrivacyNoticeChecklistItems";
import { Routes } from "../../../../root/routes";
import { useLocalStorage } from "../../../../hooks/useLocalStorage";
import { PrivacyCenterDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../../../common/hooks/useOnboardingProgress";

export type StateNoticesStep =
  | "mapWithGenerateButton"
  | "generatingNoticesLoadingIndicator"
  | "statesNoticesEditor"
  | "financialIncentivesEditor"
  | "biometricIdentifiersEditor";

export function USStateNoticesPage() {
  const editPrivacyCenter = useEditPrivacyCenter();
  const orgId = editPrivacyCenter?.props?.org?.id;
  const privacyCenterId = editPrivacyCenter?.props?.privacyCenter?.id;

  const history = useHistory();

  const stateSections = Object.values(editPrivacyCenter?.props?.privacyCenter?.anchorsByState || {})
    .map((anchor) =>
      editPrivacyCenter?.props?.privacyPolicy?.renderedPolicySections?.find(
        (section) => section.anchor === anchor,
      ),
    )
    .filter(Boolean);

  const [draftNotice, draftNoticeRequest, refreshDraftNotice] = usePrivacyCenterNotice(
    orgId,
    privacyCenterId,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
    (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
  );

  const dataRetentionModule = useDataRetentionModule();

  const { answers: incentivesAnswers } = useSurvey(orgId, CONSUMER_INCENTIVES_SURVEY_NAME);
  const offersFinancialIncentives = incentivesAnswers?.[OFFERS_CONSUMER_INCENTIVES_SLUG] === "true";
  const collectsBiometricData =
    editPrivacyCenter?.props?.privacyCenter?.collectsBiometricData || false;
  const { answers: developerInstructionsAnswers } = useSurvey(
    orgId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  const [currentStep, setCurrentStep] = useState<StateNoticesStep>("statesNoticesEditor");

  const [showFirstTimeUSStateNoticesModal, setShowFirstTimeUSStateNoticesModal] = useState(true);

  const [showStateNoticesGeneratingCompleteModal, setShowStateNoticesGeneratingCompleteModal] =
    useState(false);

  const openPreviewNotice = usePrivacyCenterPreviewPopup();

  const { updateNotice } = useUpdateNoticeApi();
  const enqueueSnackbar = useSnackBar();
  const sendPublish = async () => {
    try {
      await updateNotice({
        organizationId: orgId,
        privacyCenterId: draftNotice.privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });

      await refreshDraftNotice();
      // refresh privacy center since privacyCenter.hiddenStateAnchors may have been invalidated
      await editPrivacyCenter?.props?.privacyCenterRefresh();
    } catch (error) {
      console.error("Failed to publish the notice:", error);
      enqueueSnackbar("Failed to publish. Please try again.", { variant: "error" });
    }
  };
  const [showPublishDateModal, setShowPublishDateModal] = useState(false);
  const { isDateSaving, onDateChange } = usePrivacyCenterPublish(
    orgId,
    draftNotice,
    refreshDraftNotice,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const publishedAtNormalizedStr = draftNotice?.publishedAt?.replace(/-/g, "/");
  const publishedAtDate = moment(publishedAtNormalizedStr || new Date()).toDate();

  if (!draftNoticeRequest.success || !draftNotice || !orgId) {
    return <PageLoading />;
  }

  if (!stateSections || stateSections.length === 0) {
    return <Redirect to={Routes.privacyCenter.privacyPolicy} />;
  }

  const financialIncentiveSection = draftNotice.sections.find(
    (s) => s.anchor === "notice-of-financial-incentive",
  );
  const customRetentionPolicySection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  const biometricIdentifiersSection = draftNotice.sections.find(
    (s) => s.anchor === "colorado-biometric-identifiers-policy",
  );

  const isFinancialIncentiveSectionFinished =
    financialIncentiveSection?.content.length > 2 || isAlreadyPublished;
  const isRetentionPolicySectionFinished =
    (draftNotice.useCustomRetentionPolicySection
      ? customRetentionPolicySection?.content.length > 2
      : dataRetentionModule.isFinished) || isAlreadyPublished;
  const isBiometricIdentifiersSectionFinished = biometricIdentifiersSection?.content.length > 2;

  // Show the first time user experience (e.g. modals, footer actions) if either (1) there isn't a published notice yet OR (2) the Policies & Notices onboarding steps are not completed
  const isFirstTimeOnboarding = !(
    isAlreadyPublished && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
  );

  const { nextContent, nextOnClick, nextDisabled, showFooter } = (() => {
    if (!isFirstTimeOnboarding) {
      return {
        nextContent: null,
        nextOnClick: null,
        nextDisabled: false,
        showFooter: false,
      };
    }

    if (currentStep === "statesNoticesEditor") {
      return {
        nextContent: "Done with US Notices",
        nextOnClick: () => history.push(Routes.privacyCenter.theme),
        nextDisabled: false,
        showFooter: true,
      };
    }

    if (currentStep === "financialIncentivesEditor") {
      return {
        nextContent: "Done with Financial Notice",
        nextOnClick: () => {
          setCurrentStep("statesNoticesEditor");
          // When going back to the main US State Notices page, scroll to the top of the page
          setTimeout(() => {
            document.querySelector("div.page-content").scrollTop = 0;
          }, 300);
        },
        nextDisabled: !isFinancialIncentiveSectionFinished,
        showFooter: true,
      };
    }

    if (currentStep === "biometricIdentifiersEditor") {
      return {
        nextContent: "Done with Biometric Policy",
        nextOnClick: () => {
          setCurrentStep("statesNoticesEditor");
          // When going back to the main US State Notices page, scroll to the top of the page
          setTimeout(() => {
            document.querySelector("div.page-content").scrollTop = 0;
          }, 300);
        },
        nextDisabled: !isBiometricIdentifiersSectionFinished,
        showFooter: true,
      };
    }

    return {
      nextContent: null,
      nextOnClick: null,
      nextDisabled: false,
      showFooter: false,
    };
  })();

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showFooter ? (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent={nextContent}
                nextOnClick={nextOnClick}
                nextDisabled={nextDisabled}
              />
            }
          />
        ) : null
      }
    >
      <div>
        <div className="tw-boundary header-sticky">
          <Breadcrumbs title="Policies & Notices" sourceRoute={Routes.privacyCenter.root} />

          <PrivacyCenterTabsNew
            selectedTabKey="us-state-notices"
            enableNextTab={!nextDisabled}
            enableAllTabs={didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)}
            trailingContent={
              (currentStep === "statesNoticesEditor" ||
                currentStep === "financialIncentivesEditor" ||
                currentStep === "biometricIdentifiersEditor") && (
                <div className="flex items-center gap-2">
                  <div className="flex text-neutral-600 font-normal text-left">
                    <ProgressFooterSaveNotification saving={isSaving} isSaved={isSaved} />
                  </div>
                  <PrintPreviewPublishButtons
                    orgId={orgId}
                    privacyCenter={editPrivacyCenter?.props?.privacyCenter}
                    draftNoticePublicId={draftNotice.publicId}
                    hasPublishedNotice={
                      editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null
                    }
                    onPreviewClick={openPreviewNotice}
                    onPublishClick={() => setShowPublishDateModal(true)}
                    // In the onboarding/FTUE flow, we only enable the Publish button on the last step of the flow, so it should be disabled on this page if we're in FTUE
                    isPublishEnabled={
                      draftNotice?.sections.some(
                        (section) => !!section.header || !!section.content,
                      ) && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                    }
                    publishDisabledTooltip={
                      !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                        ? "Complete the Themes step to publish your Privacy Center."
                        : undefined
                    }
                  />
                </div>
              )
            }
          />

          {currentStep === "mapWithGenerateButton" && (
            <StatesMapWithGenerateNoticesButton
              onGenerateNoticeClick={() => {
                setCurrentStep("generatingNoticesLoadingIndicator");

                setTimeout(() => {
                  setShowStateNoticesGeneratingCompleteModal(true);
                  setCurrentStep("statesNoticesEditor");
                }, 3500);
              }}
            />
          )}

          {currentStep === "generatingNoticesLoadingIndicator" && <GeneratingNoticesLoadingStep />}
        </div>

        {(currentStep === "statesNoticesEditor" ||
          currentStep === "financialIncentivesEditor" ||
          currentStep === "biometricIdentifiersEditor") && (
          <USStateNoticesEditor
            orgId={orgId}
            privacyCenterId={privacyCenterId}
            draftNotice={draftNotice}
            stateSections={stateSections}
            refreshNotice={refreshDraftNotice}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            offersFinancialIncentives={offersFinancialIncentives}
            isFinancialIncentiveSectionFinished={isFinancialIncentiveSectionFinished}
            isRetentionPolicySectionFinished={isRetentionPolicySectionFinished}
            collectsBiometricData={collectsBiometricData}
            isBiometricIdentifiersSectionFinished={isBiometricIdentifiersSectionFinished}
            setIsSaving={setIsSaving}
            setIsSaved={setIsSaved}
          />
        )}

        {currentStep === "statesNoticesEditor" &&
          showFirstTimeUSStateNoticesModal &&
          isFirstTimeOnboarding && (
            <FirstTimeUSStateNoticesModal
              closeModal={() => setShowFirstTimeUSStateNoticesModal(false)}
            />
          )}

        {showStateNoticesGeneratingCompleteModal && (
          <Modal
            title="State notices have been successfully generated!"
            firstButton="Get Started"
            firstButtonColor="primary"
            alignButton="center"
            widthButton="full"
            maxPanelWidth="max-w-lg"
            showOverlay={true}
            closeButton={true}
            closeOnOverlayClick={true}
            onClose={() => setShowStateNoticesGeneratingCompleteModal(false)}
          >
            <p className="text-gray-500">
              Some notices may require additional setup - click below to get started.
            </p>
            <img
              src="/assets/images/stay_compliant/privacy_center/notices-generation-complete-person-with-checkbox.svg"
              className="mx-auto mt-4"
            />
          </Modal>
        )}

        {showPublishDateModal && (
          <PrivacyCenterPublishDatePickerModal
            onPublishClick={sendPublish}
            closeModal={() => setShowPublishDateModal(false)}
            publishedAtDate={publishedAtDate}
            onDateChange={onDateChange}
            disabled={isDateSaving}
          />
        )}
      </div>
    </StandardPageWrapper>
  );
}

export function PrintPreviewPublishButtons({
  orgId,
  privacyCenter,
  draftNoticePublicId,
  hasPublishedNotice,
  onPreviewClick,
  onPublishClick,
  showPdfPreviewButton = true,
  isPublishEnabled = true,
  isPreviewEnabled = true,
  previewDisabledTooltip,
  previewButtonText,
  publishDisabledTooltip,
}: {
  orgId: string;
  privacyCenter: PrivacyCenterDto;
  draftNoticePublicId?: string;
  hasPublishedNotice: boolean;
  onPreviewClick?: () => void;
  onPublishClick?: () => void;
  showPdfPreviewButton?: boolean;
  isPublishEnabled?: boolean;
  isPreviewEnabled?: boolean;
  previewDisabledTooltip?: string;
  previewButtonText?: string;
  publishDisabledTooltip?: string;
}) {
  const [hasSeenPreviewPopup, setHasSeenPreviewPopup] = useLocalStorage(
    "hasSeenPreviewPopup",
    "false",
  );
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);

  return (
    <div className="flex items-center gap-sm">
      {showPdfPreviewButton && draftNoticePublicId && privacyCenter && (
        <>
          <a
            href={ApiRouteHelpers.organization.privacyCenter.notices.noticePdf(
              orgId,
              privacyCenter?.id,
              draftNoticePublicId,
            )}
            target="_blank"
            rel="noreferrer"
            title="Download PDF"
          >
            <div
              className="bg-primary-100 hover:bg-primary-200 rounded-full cursor-pointer"
              style={{ padding: "9px" }}
            >
              <PrinterIcon className="h-6 w-6 text-primary-600" />
            </div>
          </a>
          {/* Temporary: HTML download for testing */}
          <a
            href={ApiRouteHelpers.organization.privacyCenter.notices.noticeHtml(
              orgId,
              privacyCenter?.id,
              draftNoticePublicId,
            )}
            target="_blank"
            rel="noreferrer"
            title="Download HTML"
          >
            <div
              className="bg-green-100 hover:bg-green-200 rounded-full cursor-pointer"
              style={{ padding: "9px" }}
            >
              <DocumentTextIcon className="h-6 w-6 text-green-600" />
            </div>
          </a>
        </>
      )}
      {onPreviewClick && (
        <ToolTip
          text={!isPreviewEnabled && previewDisabledTooltip}
          width="250px"
          xOffset={-60}
          yOffset={75}
        >
          <Button
            color="secondary"
            onClick={() => {
              if (hasSeenPreviewPopup !== "true" && !hasPublishedNotice) {
                setShowPreviewPopup(true);
              } else {
                onPreviewClick();
              }
            }}
            disabled={!isPreviewEnabled}
          >
            <span className="flex items-center">
              <div style={{ marginRight: "8px" }}>
                <EyeIcon className="h-6 w-6 text-primary-600" />
              </div>
              {previewButtonText || "Preview"}
            </span>
          </Button>
        </ToolTip>
      )}
      {showPreviewPopup && (
        <Modal
          title={`Previewing your Privacy Center requires that we load it to your subdomain (${privacyCenter?.defaultSubdomain}.truevaultprivacycenter.com)`}
          alignText="left"
          firstButton="Back to Editing"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setShowPreviewPopup(false)}
          secondButton="Preview Privacy Center"
          secondButtonColor="primary"
          onSecondButtonClick={() => {
            setShowPreviewPopup(false);
            setHasSeenPreviewPopup("true");
            onPreviewClick();
          }}
          showOverlay
          onClose={() => setShowPreviewPopup(false)}
          alignButton="right"
        >
          <p className="text-left text-gray-500 text-[14px] font-normal">
            It will not be directly accessible to website visitors until you complete your Developer
            Instructions in a later step.
          </p>
        </Modal>
      )}
      {onPublishClick && (
        <ToolTip
          text={
            !isPublishEnabled
              ? publishDisabledTooltip || "Make a change, then click Publish"
              : undefined
          }
          width="250px"
          xOffset={-60}
          yOffset={75}
        >
          <Button color="primary" onClick={onPublishClick} disabled={!isPublishEnabled}>
            <span className="flex items-center">
              <div style={{ marginRight: "8px" }}>
                <RocketLaunchIcon className="h-6 w-6" />
              </div>
              Publish
            </span>
          </Button>
        </ToolTip>
      )}
    </div>
  );
}

// This should be kept in sync with usHexMap.tsx in services/privacy-center
const STATES_HEX_GRID_CONFIG = [
  { q: 0, r: 0, s: 0, stateAbbr: "KY" },
  { q: 1, r: 0, s: -1, stateAbbr: "WV" },
  { q: 2, r: 0, s: -2, stateAbbr: "VA" },
  { q: 3, r: 0, s: -3, stateAbbr: "MD" },
  { q: 4, r: 0, s: -4, stateAbbr: "DE" },
  { q: -1, r: 0, s: 1, stateAbbr: "MO" },
  { q: -2, r: 0, s: 2, stateAbbr: "NE" },
  { q: -3, r: 0, s: 3, stateAbbr: "CO" },
  { q: -4, r: 0, s: 4, stateAbbr: "NV" },
  { q: -5, r: 0, s: 5, stateAbbr: "OR" },
  { q: 1, r: -1, s: 0, stateAbbr: "IN" },
  { q: 2, r: -1, s: -1, stateAbbr: "OH" },
  { q: 3, r: -1, s: -2, stateAbbr: "PA" },
  { q: 4, r: -1, s: -3, stateAbbr: "NJ" },
  { q: 5, r: -1, s: -4, stateAbbr: "CT" },
  { q: 0, r: -1, s: 1, stateAbbr: "IL" },
  { q: -1, r: -1, s: 2, stateAbbr: "IA" },
  { q: -2, r: -1, s: 3, stateAbbr: "SD" },
  { q: -3, r: -1, s: 4, stateAbbr: "WY" },
  { q: -4, r: -1, s: 5, stateAbbr: "ID" },
  { q: 2, r: -2, s: 0, stateAbbr: "MI" },
  { q: 0, r: -2, s: 2, stateAbbr: "WI" },
  { q: -1, r: -2, s: 3, stateAbbr: "MN" },
  { q: -2, r: -2, s: 4, stateAbbr: "ND" },
  { q: -3, r: -2, s: 5, stateAbbr: "MT" },
  { q: -4, r: -2, s: 6, stateAbbr: "WA" },
  { q: 4, r: -2, s: -2, stateAbbr: "NY" },
  { q: 5, r: -2, s: -3, stateAbbr: "MA" },
  { q: 6, r: -2, s: -4, stateAbbr: "RI" },
  { q: 5, r: -3, s: -2, stateAbbr: "VT" },
  { q: 6, r: -3, s: -3, stateAbbr: "NH" },
  { q: 7, r: -4, s: -3, stateAbbr: "ME" },
  { q: -4, r: -4, s: 0, stateAbbr: "AK" },
  { q: 0, r: 1, s: -1, stateAbbr: "TN" },
  { q: 1, r: 1, s: -2, stateAbbr: "NC" },
  { q: 2, r: 1, s: -3, stateAbbr: "SC" },
  { q: 3, r: 1, s: -4, stateAbbr: "DC" },
  { q: -1, r: 1, s: 0, stateAbbr: "AR" },
  { q: -2, r: 1, s: 1, stateAbbr: "KS" },
  { q: -3, r: 1, s: 2, stateAbbr: "NM" },
  { q: -4, r: 1, s: 3, stateAbbr: "UT" },
  { q: -5, r: 1, s: 4, stateAbbr: "CA" },
  { q: 0, r: 2, s: -2, stateAbbr: "AL" },
  { q: 1, r: 2, s: -3, stateAbbr: "GA" },
  { q: -1, r: 2, s: -1, stateAbbr: "MS" },
  { q: -2, r: 2, s: 0, stateAbbr: "LA" },
  { q: -3, r: 2, s: 1, stateAbbr: "OK" },
  { q: -4, r: 2, s: 2, stateAbbr: "AZ" },
  { q: 0, r: 3, s: -3, stateAbbr: "FL" },
  { q: -3, r: 3, s: 0, stateAbbr: "TX" },
  { q: -7, r: 4, s: 3, stateAbbr: "HI" },
];

function StatesMapWithGenerateNoticesButton({
  onGenerateNoticeClick,
}: {
  onGenerateNoticeClick: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <HexGrid width={850.5} height={527.85} viewBox="-40 -50 100 100">
        <Layout size={{ x: 6, y: 6 }} flat={false} spacing={1.05} origin={{ x: 0, y: 0 }}>
          {STATES_HEX_GRID_CONFIG.map(({ q, r, s, stateAbbr }) => {
            return (
              <Hexagon key={stateAbbr} q={q} r={r} s={s} className={"interactive"}>
                <Text>{stateAbbr}</Text>
              </Hexagon>
            );
          })}
        </Layout>
      </HexGrid>
      <div className="text-center" style={{ marginTop: "40px", marginRight: "111px" }}>
        <Button color="primary" onClick={onGenerateNoticeClick}>
          Generate my Notices
        </Button>
        <p className="mt-6 font-semibold" style={{ maxWidth: "400px" }}>
          We will generate your US State Notices using your Data Map and business survey responses.
        </p>
      </div>
    </div>
  );
}

function GeneratingNoticesLoadingStep() {
  return (
    <div className="flex flex-col justify-center items-center mt-6">
      <CircularProgress size="32px" />
      <p className="text-neutral-600 font-semibold text-center mt-6">
        Generating your US state notices...
      </p>
    </div>
  );
}

function FirstTimeUSStateNoticesModal({ closeModal }: { closeModal: () => void }) {
  return (
    <Modal
      title="Review your US State Notices"
      body="TrueVault generated these notices based on your data map. You can customize them or hide sections."
      firstButton="Review US Notices"
      firstButtonColor="primary"
      alignButton="center"
      widthButton="full"
      onClose={closeModal}
      showOverlay
      closeButton
    >
      <img
        src="/assets/images/stay_compliant/privacy_center/notices-generation-complete-person-with-checkbox.svg"
        className="mx-auto w-[268px] h-[211px]"
      />
    </Modal>
  );
}
